import { useEffect, useState } from 'react';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import BreadCrumbs from '../components/BreadCrumbs';
import SocialMediaShare from '../components/SocialMediaShare';
import { callGetWebservice, callPostWebservice } from '../hooks/api';
import { setCartCount } from '../store/reducers/auth';
import { getConvertedPrice, getQueryParam, saveItemObjInCart, saveItemObjInWishList } from '../utils/Utilities';
import { currencyData, userData } from '../utils/constants';
import { getFromLocalStorage } from '../utils/localStorageUtils';

const ProductDetailSkeleton = () => {
    return (
        <div className="container py-4 product-skeleton">
            <div className="row justify-content-between">
                {/* Image Section Skeleton */}
                <div className="col-xl-5 col-lg-6 col-md-12 col-sm-12">
                    <div className="skeleton-loader main-image-skeleton"></div>
                    <div className="thumbnails-skeleton">
                        {[1, 2, 3, 4].map((_, index) => (
                            <div key={index} className="skeleton-loader thumbnail-item"></div>
                        ))}
                    </div>
                </div>

                {/* Content Section Skeleton */}
                <div className="col-xl-7 col-lg-6 col-md-12 col-sm-12">
                    <div className="content-section ps-lg-4">
                        <div className="skeleton-loader category-skeleton"></div>
                        <div className="skeleton-loader title-skeleton"></div>
                        <div className="skeleton-loader price-skeleton"></div>
                        <div className="skeleton-loader description-skeleton"></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ProductDetail1 = () => {

    const merchantSettings = useSelector((state) => state.general.merchantSettings);
    const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

    const [productDetail, setProductDetail] = useState(null);
    const [price, setPrice] = useState(null);
    const [selectedVariants, setSelectedVariants] = useState({});
    const [errorMsg, setErrorMsg] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [selectedQuantity, setSelectedQuantity] = useState(1);

    let currency = getFromLocalStorage(currencyData);

    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        const productId = getQueryParam(location, "product_id");

        let requestPacket = {
            'item_id': productId
        };

        callGetWebservice('my-item-detail', requestPacket, (response) => {
            handleProductDetailWebservice(response);

        }, null, dispatch, navigate);
    }, []);

    const handleProductDetailWebservice = (response) => {
        setProductDetail(response.data);

        if (response.data.has_variants === 0) {
            let itemVariants = response.data.item_variants[0];
            setPrice({
                price: itemVariants.price,
                salePrice: itemVariants.sale_price,
                stock: itemVariants.stock_status
            })
        }
    }

    const checkIfVariantsAreSelected = () => {

        if (productDetail === null || productDetail.length === 0)
            return false;

        const { item_attributes = [] } = productDetail;
        const requiredAttributes = item_attributes.length;
        const selectedAttributes = Object.keys(selectedVariants).length;

        return selectedAttributes === requiredAttributes ? true : false;
    }

    const validateVariants = (type) => {

        if (!checkIfVariantsAreSelected()) {
            if (type === 'cart')
                setErrorMsg('Please select all options (Size, Color, etc.) before adding to cart.');
            else
                setErrorMsg('Please select all options before adding to wishlist.');
            return false;
        }

        setErrorMsg(null);
        return true;
    };

    const handleVariantChange = (attributeId, valueId) => {
        setSelectedVariants((prev) => ({
            ...prev,
            [attributeId]: valueId
        }));
    };

    useEffect(() => {
        if (checkIfVariantsAreSelected()) {
            updatePrice();
        }
    }, [selectedVariants])

    const getSelectedVariant = () => {
        const selectedVariant = productDetail?.item_variants.find(variant => {
            return variant.item_variant_attributes.every(attr => {
                const selectedValue = selectedVariants[attr.item_attribute_value?.item_attribute_id];
                return selectedValue === attr.item_attribute_value_id;
            });
        });
        return selectedVariant;
    }

    const updatePrice = () => {
        const selectedVariant = getSelectedVariant();
        if (selectedVariant) {
            setPrice({
                price: selectedVariant.price,
                salePrice: selectedVariant.sale_price,
                stock: selectedVariant.stock_status
            })
        }
    };

    const handleAddToCart = (type) => {
        const user = getFromLocalStorage(userData);
        if (!validateVariants(type)) return;

        const selectedVariant = getSelectedVariant();
        const enrichedVariant = {
            ...selectedVariant,
            item_variant_attributes: selectedVariant.item_variant_attributes.map(attr => {
                const attribute = productDetail.item_attributes?.find(a => a.id === attr.item_attribute_value?.item_attribute_id);
                return {
                    ...attr,
                    name: attribute ? attribute.name : 'Unknown'
                };
            })
        };

        if (selectedVariant.stock_status !== 'In Stock' && merchantSettings.config.is_trackable_quantity === 1) {
            setErrorMsg('Sorry, this specific combination is currently Out of Stock.');
            return;
        }

        let currency = getFromLocalStorage(currencyData);

        let addProductObj = {
            ...productDetail,
            selected_variant: enrichedVariant,
            quantity: selectedQuantity,
            currency_id: currency?.currency.id
        };

        if (type === 'cart')
            saveItemInCart(addProductObj);
        else
            saveItemInWishList(addProductObj);
    }

    const saveItemInCart = (addProductObj) => {
        let result = saveItemObjInCart(addProductObj);
        if (!isLoggedIn) {
            dispatch(setCartCount(result.cartCount));
            navigate("/cart");
            return;
        }

        let products = result.cart;
        let currency = getFromLocalStorage(currencyData);
        let requestPacket = {
            products,
            currency_id: currency?.currency.id,
            outlet_id: merchantSettings?.outlet?.id
        }

        callPostWebservice('add-to-cart', requestPacket, (response) => {
            dispatch(setCartCount(result.cartCount));
            navigate("/cart");
        }, (error) => {
            alert(error.errMessage);
        }, dispatch, navigate);
    }

    const saveItemInWishList = (addProductObj) => {
        let result = saveItemObjInWishList(addProductObj);
        let products = result.wish;
        let requestPacket = { products }
        callPostWebservice('add-to-wish', requestPacket, (response) => {
            navigate("/wish");
        }, (error) => {
            alert(error.errMessage);
        });
    }

    if (!productDetail) {
        return (
            <>
                <BreadCrumbs title1={'Home'} title2={'Category'} title3={'Product'} />
                <section className="middle bg-white">
                    <ProductDetailSkeleton />
                </section>
            </>
        );
    }

    const {
        category,
        description,
        item_attributes = [],
        name,
        image,
        media = []
    } = productDetail;

    const currentUrl = window.location.href;
    const cleanDescription = description ? description.replace(/<[^>]+>/g, '').substring(0, 160) : '';
    const mainImageUrl = media.length > 0 ? media[0]?.original_url : image;
    const storeName = merchantSettings?.legal_name || 'Kumo';
    const pageTitle = `${name} | ${storeName}`;

    return (
        <HelmetProvider>
            <div className="bg-white">
                <Helmet>
                    <title>{pageTitle}</title>
                    <meta name="description" content={cleanDescription} />
                    <meta property="og:title" content={pageTitle} />
                    <meta property="og:description" content={cleanDescription} />
                    {mainImageUrl && <meta property="og:image" content={mainImageUrl} />}
                    <meta property="og:url" content={currentUrl} />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content={pageTitle} />
                    <meta name="twitter:description" content={cleanDescription} />
                    {mainImageUrl && <meta name="twitter:image" content={mainImageUrl} />}
                    <link rel="canonical" href={currentUrl} />
                </Helmet>

                <BreadCrumbs
                    title1={'Home'}
                    title2={category?.name || 'Category'}
                    title3={name}
                />

                <section className="middle">
                    <div className="container">
                        <div className="row justify-content-between">

                            {/* Product Gallery */}
                            <div className="col-xl-5 col-lg-6 col-md-12 col-sm-12">
                                <div className="product-gallery-modern">
                                    <div className="main-img-container" onClick={() => setIsLightboxOpen(true)}>
                                        {media.length > 0 && (
                                            <img
                                                src={media[currentImageIndex]?.original_url}
                                                className="img-fluid w-100"
                                                alt={name || ''}
                                            />
                                        )}
                                    </div>

                                    <div className="thumb-scroll">
                                        {media.map((image, index) => (
                                            <div
                                                key={index}
                                                className={`thumb-item-modern ${currentImageIndex === index ? 'active' : ''}`}
                                                onClick={() => setCurrentImageIndex(index)}
                                            >
                                                <img
                                                    src={image.original_url}
                                                    alt={`Thumbnail ${index + 1}`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Lightbox */}
                                {isLightboxOpen && media.length > 0 && (
                                    <Lightbox
                                        mainSrc={media[currentImageIndex]?.original_url}
                                        nextSrc={media[(currentImageIndex + 1) % media.length]?.original_url}
                                        prevSrc={media[(currentImageIndex + media.length - 1) % media.length]?.original_url}
                                        onCloseRequest={() => setIsLightboxOpen(false)}
                                        onMovePrevRequest={() =>
                                            setCurrentImageIndex((currentImageIndex + media.length - 1) % media.length)
                                        }
                                        onMoveNextRequest={() =>
                                            setCurrentImageIndex((currentImageIndex + 1) % media.length)
                                        }
                                    />
                                )}
                            </div>

                            {/* Product Info */}
                            <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12">
                                <div className="prd_details ps-lg-5">

                                    <div className="mb-3">
                                        <span className="badge-premium">{category?.name}</span>
                                    </div>

                                    <div className="mb-3">
                                        <h2 className="ft-bold mb-1">{name}</h2>

                                        {price && currency && (
                                            <div className="price-box-modern">
                                                <span className="old-price">{`${currency?.currency?.symbol} ${getConvertedPrice(price.price)}`}</span>
                                                <span className="new-price">{`${currency?.currency?.symbol} ${getConvertedPrice(price.salePrice)}`}</span>
                                                {merchantSettings.config.is_trackable_quantity === 1 && (
                                                    <span className={`badge ml-3 ${price?.stock === 'In Stock' ? 'bg-success' : 'bg-danger'} text-light`}>
                                                        {price?.stock}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="prd-description-modern">
                                        <p dangerouslySetInnerHTML={{ __html: description }} />
                                    </div>

                                    {/* Variants Section */}
                                    {item_attributes?.map((attribute) => (
                                        <div className="variant-selector-modern" key={attribute.id}>
                                            <span className="variant-title">{attribute.name}</span>
                                            <div className="chip-container">
                                                {attribute?.attribute_values.map((value) => (
                                                    <label key={value.id} className="chip-option">
                                                        <input
                                                            type="radio"
                                                            name={`attr-${attribute.id}`}
                                                            value={value.id}
                                                            checked={selectedVariants[attribute.id] === value.id}
                                                            onChange={() => handleVariantChange(attribute.id, value.id)}
                                                        />
                                                        <span className="chip-label">{value.value}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    ))}

                                    {errorMsg && <div className="error-modern"><i className="fas fa-exclamation-circle mr-2"></i>{errorMsg}</div>}

                                    <div className="quantity-container-modern">
                                        <span className="variant-title mb-0">Qty:</span>
                                        <select
                                            className="qty-select-premium"
                                            value={selectedQuantity}
                                            onChange={(e) => setSelectedQuantity(Number(e.target.value))}
                                        >
                                            {[1, 2, 3, 4, 5, 10].map(n => (
                                                <option key={n} value={n}>{n}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="action-buttons-grid">
                                        <button
                                            type="button"
                                            className="btn-product-action btn-cart-premium"
                                            onClick={() => handleAddToCart('cart')}
                                        >
                                            <i className="fas fa-shopping-bag mr-2"></i>Add to Cart
                                        </button>

                                        {isLoggedIn && (
                                            <button
                                                type="button"
                                                className="btn-product-action btn-wish-premium"
                                                onClick={() => handleAddToCart('wish')}
                                            >
                                                <i className="far fa-heart"></i>
                                            </button>
                                        )}
                                    </div>

                                    <div className="mt-5 pt-4 border-top">
                                        <p className="mb-0 d-flex align-items-center">
                                            <span className="mr-3 text-muted ft-medium">Share Product:</span>
                                            <SocialMediaShare url={image} title={name} description={description} />
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Product Description Tabs */}
                <section className="middle bg-light-soft mt-5">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-xl-11 col-lg-12">
                                <ul className="nav nav-tabs b-0 d-flex align-items-center justify-content-center simple_tab_links mb-4" id="productTabs">
                                    <li className="nav-item">
                                        <a className="nav-link active ft-bold" data-toggle="tab" href="#description">Details & Specification</a>
                                    </li>
                                </ul>

                                <div className="tab-content glass-card p-4 p-lg-5">
                                    <div className="tab-pane fade show active" id="description">
                                        <div className="prd-description-modern text-center mx-auto" style={{ maxWidth: '800px' }}>
                                            <p dangerouslySetInnerHTML={{ __html: productDetail?.description }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </HelmetProvider>
    )
}

export default ProductDetail1